import PropTypes from 'prop-types';
import { get, isEmpty, compact, merge, pick, round, isNil } from 'lodash-es';
import {
  constructOfferObject,
  getOfferCountTrackingMap,
  getOfferDisplayCountTrackingMap,
  getMultiOfferResultsObject,
  getOfferDetailsPageObject,
  getSolicitedPartners,
  getSolicitedPartnersTrackingMap,
  getOfferBrandsTrackingMap,
  getSellerAbTestTrackingMap,
  getMultiOfferTrackingMap,
  getOffersPendingTrackingMap,
  getDeclineReasonTrackingMap,
  getIcoOfferTrackingMap,
  getModsSourceTrackingMap,
  findDealerInvoicingAccount,
  prepareModsRecord,
  processMultiOfferResultsOffers,
  acceptedOfferFilter,
  fetchAndProcessQuestions,
  getOffersFromModsRecord,
} from 'client/data/utils/multi-offer';
import { VEHICLE_CONDITION_CQ_ID } from 'client/data/constants/multi-offer';
import { objectToQueryString } from 'site-modules/shared/utils/string';
import { parseStoreRawData } from 'site-modules/multi-offer/utils/offers';
import { transformQuestions } from 'client/data/transforms/appraisal/transform-questions';
import { buildAllTmvBandsUrl } from 'client/data/models/vehicle-v2';
import { getAllUsedTmvConditions } from 'client/data/utils/used-tmv';
import { EdmundsGraphQLFederation, getErrorStatusCode } from 'client/data/graphql/graphql-client';
import { checkModsRecordForAbTestRecipe } from 'site-modules/multi-offer/utils/ab-tests';
import { createModelSegment } from 'client/data/luckdragon/segment';
import { withMetrics } from 'client/data/api/api-metrics';
import { EdmundsMultiOfferAPI, EdmundsAPI } from 'client/data/api/api-client';
import { BRANDS, ICO_SOURCES } from 'site-modules/multi-offer/constants/brands';
import { PARTNERS } from 'site-modules/shared/constants/multi-offer/partners';
import { logger } from 'client/utils/logger/logger';
import { buildClosestIcoDealersPath, DealerIcoWidgetModel } from 'client/data/models/dealer-ico-widget';
import { hasMultiOfferPartners } from 'site-modules/shared/utils/multi-offer/multi-offer';
import { isExpiredOffer } from 'site-modules/shared/utils/multi-offer/offers';
import { removeSpecialCharactersForEdwPixel } from 'site-modules/shared/utils/tracking';
import { PageModel } from 'client/data/models/page';
import { InventoryModel } from 'client/data/models/inventory';
import { PAGE_NAMES } from 'site-modules/shared/constants/multi-offer/offers';
import { getQuery } from 'client/utils/location';

export const VENOM_X_PRODUCT_ID_HEADER = { 'X-Product-Id': 'venom' };

export const sendEmailFromMods = async ({ modsId, email, data }) => {
  const payload = {
    email,
    ...data,
  };

  try {
    await EdmundsMultiOfferAPI.fetchJson(`/v1/offer-events/${modsId}/email`, {
      headers: { ...VENOM_X_PRODUCT_ID_HEADER, 'content-type': 'application/json' },
      retries: 1,
      method: 'POST',
      body: JSON.stringify(payload),
      includeResponseInError: true,
    });
    return true;
  } catch (e) {
    logger.warn(e, 'Failed send email to MODS');
    return false;
  }
};

const getErrorMessage = responseText => {
  if (!responseText) {
    // It's useful to know exactly what "empty" response we have.
    return responseText;
  }
  try {
    const json = JSON.parse(responseText);
    return json && json.message;
  } catch {
    return removeSpecialCharactersForEdwPixel(responseText);
  }
};

export const createModsRecord = async (payload, options = {}) => {
  let response = {};

  try {
    response = await EdmundsMultiOfferAPI.fetchJson('/v1/offer-events', {
      headers: { ...options.headers, ...VENOM_X_PRODUCT_ID_HEADER, 'content-type': 'application/json' },
      retries: 1,
      method: 'POST',
      body: JSON.stringify(payload),
      includeResponseInError: true,
    });
  } catch (e) {
    logger.warn(e, 'Failed multi-offer record creation');
    response = { isError: true, message: getErrorMessage(e.responseText) };
  }

  return response;
};

export const makePrequalifyRequest = async (stateCode, dma, year, mileage, makeSlug, context) => {
  const inputs = { stateCode, dma };
  if (year) inputs.year = +year;
  if (mileage) inputs.mileage = +mileage;
  if (makeSlug) inputs.makeSlug = makeSlug;

  const api = context ? withMetrics(EdmundsMultiOfferAPI, context) : EdmundsMultiOfferAPI;

  try {
    return await api.fetchJson(`/v1/partners/prequalify`, {
      headers: {
        ...VENOM_X_PRODUCT_ID_HEADER,
        'content-type': 'application/json',
      },
      method: 'POST',
      body: JSON.stringify({ inputs }),
      showAPIError: true,
    });
  } catch (e) {
    logger.warn(e, `Failed multi-offer prequalify for stateCode=${stateCode} dma=${dma} mileage=${mileage}`);
    return context ? {} : { isError: true, message: getErrorMessage(e.responseText) };
  }
};

export const solicitMoreOffers = async (modsId, payload, options = {}) => {
  let response = {};
  try {
    response = await EdmundsMultiOfferAPI.fetchJson(`/v1/offer-events/${modsId}/solicit-more-offers`, {
      headers: { ...options.headers, ...VENOM_X_PRODUCT_ID_HEADER, 'content-type': 'application/json' },
      retries: 1,
      method: 'POST',
      body: JSON.stringify(payload),
      showAPIError: true,
    });
  } catch (e) {
    logger.warn(e, 'Failed multi-offer solicitation for more offers');
    response = { isError: true, message: e.message, status: e.status };
  }

  return response;
};

export const solicitOffersV2 = async (payload, options = {}) => {
  let response = {};
  try {
    response = await EdmundsMultiOfferAPI.fetchJson(`/v2/offer`, {
      headers: { ...options.headers, ...VENOM_X_PRODUCT_ID_HEADER, 'content-type': 'application/json' },
      retries: 1,
      method: 'POST',
      body: JSON.stringify(payload),
      showAPIError: true,
    });
  } catch (e) {
    logger.warn(e, 'Failed multi-offer V2 solicitation');
    response = { isError: true, message: e.message, status: e.status, responseBody: e.custom };
  }

  return response;
};

export const requestRedemptionInfo = async (modsId, payload, options = {}) => {
  let response = {};
  try {
    response = await EdmundsMultiOfferAPI.fetchJson(`/v1/offer-events/${modsId}/request-redemption-info`, {
      headers: { ...options.headers, ...VENOM_X_PRODUCT_ID_HEADER, 'content-type': 'application/json' },
      retries: 1,
      method: 'POST',
      body: JSON.stringify(payload),
      showAPIError: true,
    });
  } catch (e) {
    logger.warn(e, 'Failed multi-offer request redemption info');
    response = { isError: true, message: e.message, status: e.status };
  }

  return response;
};

export const appendAbTestBucketing = async (modsId, payload, options = {}) => {
  try {
    return await EdmundsMultiOfferAPI.fetchJson(`/v1/offer-events/${modsId}/append-abtest`, {
      headers: { ...options.headers, ...VENOM_X_PRODUCT_ID_HEADER, 'content-type': 'application/json' },
      retries: 1,
      method: 'PATCH',
      body: JSON.stringify(payload),
      showAPIError: true,
    });
  } catch (e) {
    logger.warn(e, 'Failed multi-offer append AB test bucketing');
    return { isError: true, message: e.message, status: e.status };
  }
};

export const getTmvByModsData = async (modsData, userMileage, options = {}) => {
  try {
    const vehicle = get(modsData, 'vehicle');
    const zipCode = get(modsData, 'seller.zipCode');

    if (!zipCode || isEmpty(vehicle) || !userMileage) return null;

    const apiUrl = buildAllTmvBandsUrl({
      styleId: vehicle.edmundsStyleId,
      zipCode,
      mileage: userMileage,
      colorId: get(vehicle, 'colors.exterior.id'),
      optionIds: compact(get(vehicle, 'selectedOptions', []).map(({ id }) => id)).join(','),
    });

    return await EdmundsAPI.fetchJson(apiUrl, {
      headers: { ...options.headers },
      showAPIError: true,
    });
  } catch (e) {
    logger.warn(e, 'Failed TMV request');
    return { isError: true, message: e.message, status: e.status };
  }
};

const getIcoOfferFromModsRecord = data => data?.partners?.[BRANDS.ICO]?.offers?.[0] ?? {};

const OfferDetailsEntity = PropTypes.shape({
  code: PropTypes.string,
  offerUrl: PropTypes.string,
  valuation: PropTypes.number,
  estimate: PropTypes.shape({
    source: PropTypes.string,
    valuation: PropTypes.number,
    category: PropTypes.shape({
      'below-average': PropTypes.number,
      average: PropTypes.number,
      'above-average': PropTypes.number,
      excellent: PropTypes.number,
    }),
  }),
  printUrl: PropTypes.string,
  store: PropTypes.shape({
    // Store object that contains parsed store data (fields like name, city, etc.).
    // NOTE: 'rawData' field from MODS (which contains raw/stringified data) is removed in favor of 'data'.
    data: PropTypes.shape({}),
  }),
  expiresDisplayDate: PropTypes.string,
  expiresDateUtc: PropTypes.string,
  status: PropTypes.string,
  brand: PropTypes.string,
  source: PropTypes.string,
  partner: PropTypes.string,
  /**
   * Number of the offer as it's stored in the array from model (i.e. the highest offer is the 1st).
   * Starts with 1.
   */
  offerNumber: PropTypes.number,
  /**
   * Number of the offer as it's displayed on the page (i.e. the highest offer MAY NOT be the 1st).
   * Starts with 1.
   */
  offerDisplayNumber: PropTypes.number,
  isObscured: PropTypes.bool,
  /**
   * Flag that indicates if the redemption info has been requested for this offer.
   *
   * NOTE: this field comes from MODS.
   */
  offerOfInterest: PropTypes.bool,
  /**
   * Http status (e.g. 200) of the response for the request redemption info call.
   * May be null if the call failed without response (e.g. network error).
   * If the call wasn't made then this should be undefined.
   *
   * NOTE: this field comes from MODS.
   */
  redemptionInfoResponse: PropTypes.number,
  /**
   * Indicates the offer state in relation to requesting the redemption info (which includes submitting PII).
   *
   * NOTE: this field is computed in FE code.
   */
  redemptionState: PropTypes.string,
  /**
   * Dealer name, currently present for ICO & ICOMODULE offers.
   *
   * NOTE: this field is computed in FE code.
   */
  dealerName: PropTypes.string,
});

const VehicleDetailsEntity = PropTypes.shape({
  chromeStyleId: PropTypes.string,
  condition: PropTypes.string,
  drive: PropTypes.string,
  edmundsStyleId: PropTypes.number,
  engineSize: PropTypes.number,
  exteriorColor: PropTypes.string,
  interiorColor: PropTypes.string,
  kmxStyleSku: PropTypes.string,
  make: PropTypes.string,
  makeSlug: PropTypes.string,
  mileage: PropTypes.number,
  model: PropTypes.string,
  modelSlug: PropTypes.string,
  selectedOptions: PropTypes.arrayOf(
    PropTypes.shape({
      description: PropTypes.string,
      oemCode: PropTypes.string,
    })
  ),
  styleName: PropTypes.string,
  subModel: PropTypes.string,
  subModelSlug: PropTypes.string,
  tmv: PropTypes.shape({
    average: PropTypes.number,
    outstanding: PropTypes.number,
    rough: PropTypes.number,
    clean: PropTypes.number,
  }),
  transmissionType: PropTypes.string,
  vin: PropTypes.string,
  year: PropTypes.number,
});

const QuestionsEntity = PropTypes.arrayOf(
  PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    desc: PropTypes.string,
    category: PropTypes.string,
    answerType: PropTypes.string,
    answers: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
        desc: PropTypes.string,
        answerType: PropTypes.string,
        detailQuestions: PropTypes.arrayOf(
          PropTypes.shape({
            id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
            desc: PropTypes.string,
            answerType: PropTypes.string,
            answers: PropTypes.arrayOf(PropTypes.shape({})),
            partner: PropTypes.string,
          })
        ),
      })
    ),
    partner: PropTypes.string,
  })
);

const IcoOfferEntity = PropTypes.shape({
  valuation: PropTypes.number.isRequired,
  id: PropTypes.number.isRequired,
  name: PropTypes.string.isRequired,
  address: PropTypes.shape({
    zip: PropTypes.string,
    country: PropTypes.string,
    city: PropTypes.string,
    stateName: PropTypes.string,
    street: PropTypes.string,
    county: PropTypes.string,
    stateCode: PropTypes.string,
  }).isRequired,
  phoneNumbers: PropTypes.shape({
    basic: PropTypes.shape({
      areaCode: PropTypes.string,
      prefix: PropTypes.string,
      postfix: PropTypes.string,
    }).isRequired,
  }),
  showroomPage: PropTypes.string,
  source: PropTypes.oneOf([ICO_SOURCES.MODS]).isRequired,
});

export const MultiOfferEntities = {
  QuestionsData: PropTypes.shape({
    questions: QuestionsEntity,
  }),
  Questions: QuestionsEntity,
  OfferDetails: OfferDetailsEntity,
  VehicleDetails: VehicleDetailsEntity,
  OffersResultsDetails: PropTypes.shape({
    offers: PropTypes.arrayOf(OfferDetailsEntity),
    vehicle: VehicleDetailsEntity,
    pageloadTrackingBySubactionName: PropTypes.shape({}),
    renderType: PropTypes.string,
    createdAt: PropTypes.string,
    updatedAt: PropTypes.string,
    seller: PropTypes.shape({
      zipCode: PropTypes.string,
    }),
  }),
  OfferDetailsPageData: PropTypes.shape({
    offer: OfferDetailsEntity,
    vehicle: VehicleDetailsEntity,
    pageloadTrackingBySubactionName: PropTypes.shape({}),
    renderType: PropTypes.string,
    seller: PropTypes.shape({
      zipCode: PropTypes.string,
    }),
  }),
  IcoOffer: IcoOfferEntity,
  source: PropTypes.string,
};

const ALL_MAKES_FILTER = 'all';
const DEFAULT_ARRAY = [];
const DEFAULT_INVENTORY = {
  totalNumber: 0,
  results: DEFAULT_ARRAY,
};

export function getInventoryModuleUrl({
  make,
  zipCode,
  latitude,
  longitude,
  dma,
  sortBy,
  stateCode,
  national,
  radius,
}) {
  return `/purchasefunnel/v1/srp/inventory?${objectToQueryString({
    seoInfo: false,
    pageSize: 4,
    fetchFacets: false,
    make: make !== ALL_MAKES_FILTER ? make : undefined,
    radius,
    zip: zipCode,
    lat: latitude,
    lon: longitude,
    dma,
    sortby: sortBy,
    stateCode,
    national,
  })}`;
}

export const questionsPath = {
  build: () => 'questions', // Used specifically to "build" the model segment path. Example: `zipCode["${zipCode}"].make["${make}"].model["${model}"].year["${year}"]`
  segment: 'questions', // Used specifically for model segment. Example: 'zipCode["{zipCode}"].make["{make}"].model["{model}"].year["{year}"]'
};

export const questionsV2Path = {
  build: () => 'questionsV2',
  segment: 'questionsV2',
};

/**
 * Multi-offer questions transformed for usage in VAC Step 4.
 */
export const transformedQuestionsPath = {
  build: () => 'transformedQuestions',
  segment: 'transformedQuestions',
};

/**
 * Multi-offer V2 questions transformed for usage in VAC Step 4.
 */
export const transformedQuestionsV2Path = {
  build: () => 'transformedQuestionsV2',
  segment: 'transformedQuestionsV2',
};

/**
 * Multi-offer V2 questions transformed for usage in MCP tool.
 */
export const transformedQuestionsMcpPath = {
  build: () => 'transformedQuestionsMcp',
  segment: 'transformedQuestionsMcp',
};

/**
 * Raw MODS (Multi-Offer Data Store) record path.
 *
 * This path contains the MODS record as it is returned by MOWS, i.e. WITHOUT any post-processing.
 *
 * IMPORTANT: this path is intended ONLY for the WebSocket functionality.
 */
export const rawModsRecordPath = {
  build: modsId => `modsId["${modsId}"].rawRecord`,
  segment: 'modsId["{modsId}"].rawRecord',
};

/**
 * MODS (Multi-Offer Data Store) record path.
 *
 * This path is ready to be used by the UI components.
 */
export const modsRecordPath = {
  build: modsId => `modsId["${modsId}"].record`,
  segment: 'modsId["{modsId}"].record',
};

/**
 * MOR (Multi-Offer Results) path.
 */
export const offersResultsPath = {
  build: modsId => `modsId["${modsId}"].results`,
  segment: 'modsId["{modsId}"].results',
};

/**
 * ODP (Offer Details Page) path.
 */
export const offerDetailsPath = {
  build: modsId => `modsId["${modsId}"].offerDetails`,
  segment: 'modsId["{modsId}"].offerDetails',
};

/**
 * Special path for 'seller' and 'vehicle' data from MODS, that are required to build TMV call params.
 */
export const tmvParamsPath = {
  build: () => 'tmvParams',
  segment: 'tmvParams',
};

export const tmvDataPath = {
  build: modsId => `modsId["${modsId}"].tmvData`,
  segment: 'modsId["{modsId}"].tmvData',
};

export const searchByVinPath = {
  build: vin => `vin["${vin}"].searchByVin`,
  segment: `vin["{vin}"].searchByVin`,
};

export const offerRenewalModsDataPath = {
  build: ({ vin, modsId }) => `vin["${vin}"].modsId["${modsId || 'null'}"].offerRenewalModsData`,
  segment: `vin["{vin}"].modsId["{modsId}"].offerRenewalModsData`,
};

export const prequalificationPath = {
  build: ({ stateCode, dma, year, mileage, makeSlug }) =>
    `stateCode["${stateCode}"].dma["${dma}"].year["${year}"].mileage["${mileage}"].makeSlug["${makeSlug}"].prequalification`,
  segment: `stateCode["{stateCode}"].dma["{dma}"].year["{year}"].mileage["{mileage}"].makeSlug["{makeSlug}"].prequalification`,
};

export const icoDealerPath = {
  build: ({ zipCode }) => (zipCode ? `zipCode["${zipCode}"].icoDealer` : ''),
  segment: `zipCode["{zipCode}"].icoDealer`,
};

export const icoOfferPath = {
  build: modsId => `modsId["${modsId}"].icoOffer`,
  segment: 'modsId["{modsId}"].icoOffer',
};

export const inventoryModulePath = {
  build: make => `inventoryModule["${make || ALL_MAKES_FILTER}"]`,
  segment: 'inventoryModule["{make}"]',
};

export const partnersBrandAssetsPath = {
  build: () => 'partners.brandAssets',
  segment: 'partners.brandAssets',
};

const transformToModsData = modsRecord => {
  const { vehicle, conditionDetails, seller, createdAt } = modsRecord;
  const offers = getOffersFromModsRecord(modsRecord);
  const kmxConditionQuestions = JSON.parse(get(conditionDetails, 'kmx.rawData', '[]'));
  const carwiserConditionQuestions = JSON.parse(get(conditionDetails, 'carwiser.rawData', '[]'));
  const v2ConditionQuestions = JSON.parse(get(conditionDetails, 'v2.rawData', '{}'));
  const offersFinalizedAt = get(modsRecord, 'partners.kmx.offersFinalizedAt');

  return {
    createdAt,
    vehicle: {
      ...vehicle,
      vin: modsRecord.vin,
    },
    kmxConditionQuestions,
    v2ConditionQuestions,
    seller,
    offers,
    kmx: {
      offersFinalizedAt,
      offerDetails: get(modsRecord, 'partners.kmx.offers[0]'),
    },
    carwiser: {
      conditionQuestions: carwiserConditionQuestions,
    },
  };
};

export const MultiOfferModel = createModelSegment('multiOffer', [
  {
    path: questionsPath.segment,
    async resolve(match, context) {
      const location = await context.resolveValue('location', PageModel);
      const isE2E = getQuery(location).e2e === 'true';

      const endpoint = isE2E ? '/v1/mock/condition-questions' : '/v1/condition-questions';
      const errorContext = isE2E
        ? 'Failed retrieving multi-offer E2E condition questions'
        : 'Failed retrieving multi-offer condition questions';

      return fetchAndProcessQuestions({
        endpoint,
        errorContext,
        context,
      });
    },
  },
  {
    path: questionsV2Path.segment,
    async resolve(match, context) {
      const location = await context.resolveValue('location', PageModel);
      const isE2E = getQuery(location).e2e === 'true';

      const endpoint = isE2E ? '/v2/mock/condition-questions' : '/v2/condition-questions';
      const errorContext = isE2E
        ? 'Failed retrieving multi-offer E2E V2 condition questions'
        : 'Failed retrieving multi-offer V2 condition questions';

      const result = await fetchAndProcessQuestions({
        endpoint,
        errorContext,
        context,
        shouldSort: false,
      });
      if (result && !result.isError) {
        // V2 endpoint returns question array wrapped into an object with the key "questions".
        result.questions = result.questions?.questions;
      }
      return result;
    },
  },
  {
    path: transformedQuestionsPath.segment,
    async resolve(match, context) {
      const questionsData = await context.resolveValue(questionsPath.build());
      const questions = get(questionsData, 'questions', []);
      return transformQuestions(questions);
    },
  },
  {
    path: transformedQuestionsV2Path.segment,
    async resolve(match, context) {
      const questionsData = await context.resolveValue(questionsV2Path.build());
      const questions = get(questionsData, 'questions', []);
      const isError = !!questionsData?.isError;

      // The vehicle_condition question is NOT displayed on VAC Step 4, since that data is collected on Step 3.
      const filteredQuestions = questions.filter(q => q.id !== VEHICLE_CONDITION_CQ_ID);
      return {
        ...transformQuestions(filteredQuestions),
        ...(isError && {
          isError,
          message: questionsData.message,
          status: questionsData.status,
        }),
      };
    },
  },
  {
    path: transformedQuestionsMcpPath.segment,
    async resolve(match, context) {
      const questionsData = await context.resolveValue(questionsV2Path.build());
      const questions = get(questionsData, 'questions', []);
      return transformQuestions(questions);
    },
  },
  {
    path: rawModsRecordPath.segment,
    async resolve(match, context) {
      const { modsId } = match;

      try {
        // IMPORTANT: Using `await` here is critical, otherwise the error will NOT be thrown/caught HERE if the mods
        // record is not found (and as a result, the "custom 404 page" feature will not work).
        return await withMetrics(EdmundsMultiOfferAPI, context).fetchJson(`/v1/offer-events/${modsId}`, {
          headers: {
            ...VENOM_X_PRODUCT_ID_HEADER,
          },
        });
      } catch (e) {
        logger.warn(e, `Failed retrieving MODS record from ${modsId}`);
        return {};
      }
    },
  },
  {
    path: modsRecordPath.segment,
    async resolve(match, context) {
      const { modsId } = match;

      const record = await context.resolveValue(rawModsRecordPath.build(modsId));

      if (isEmpty(record)) {
        return {};
      }

      const results = prepareModsRecord(record);

      /**
       * Update parameters for TMV so it can be used and cached. Cache should bust when the actual data is changed.
       * See EMO-913 for more details.
       */
      await context.updateValue(tmvParamsPath.build(), { seller: results.seller, vehicle: results.vehicle });

      return results;
    },
  },
  {
    path: offersResultsPath.segment,
    async resolve(match, context) {
      const { modsId } = match;
      const pageloadTrackingBySubactionName = {};
      let partnerData = {};
      let seller = {};

      const data = await context.resolveValue(modsRecordPath.build(modsId), MultiOfferModel);
      if (isEmpty(data)) {
        return getMultiOfferResultsObject({ isError: true });
      }
      partnerData = get(data, 'partners', {});
      const kmxPartnerObject = partnerData[BRANDS.KMX];
      const kmxOffer = get(kmxPartnerObject, ['offers', 0]);
      const icoOffer = getIcoOfferFromModsRecord(data);
      const createdAt = get(data, 'createdAt');
      const updatedAt = get(data, 'updatedAt');
      seller = get(data, 'seller');
      const source = data?.source;

      const vehicle = get(data, ['vehicle'], {});

      const solicitedPartners = getSolicitedPartners(get(data, 'partnersToSolicit'));

      const { offersArePending, displayedOffers, nonKmxOffers } = processMultiOfferResultsOffers(data);

      // Includes page load tracking for BOTH pending and offer finalized.
      merge(
        pageloadTrackingBySubactionName,
        getSolicitedPartnersTrackingMap(solicitedPartners),
        getSellerAbTestTrackingMap(seller),
        getDeclineReasonTrackingMap(nonKmxOffers, BRANDS.ECHOPARK, PARTNERS.ECHOPARK),
        getDeclineReasonTrackingMap(nonKmxOffers, BRANDS.PLUG, PARTNERS.PLUG),
        getModsSourceTrackingMap(source)
      );

      const partnersOffersFinalizedAt = data?.partnersOffersFinalizedAt;

      if (offersArePending) {
        merge(pageloadTrackingBySubactionName, getOffersPendingTrackingMap());
        return getMultiOfferResultsObject({
          offers: displayedOffers,
          vehicle,
          pageloadTrackingBySubactionName,
          offersArePending,
          createdAt,
          updatedAt,
          seller,
          source,
          partnersOffersFinalizedAt,
        });
      }

      // Includes page load tracking when offers are finalized (no longer pending)
      merge(
        pageloadTrackingBySubactionName,
        getOfferCountTrackingMap(displayedOffers, data, nonKmxOffers),
        getOfferDisplayCountTrackingMap(displayedOffers.filter(acceptedOfferFilter)),
        getOfferBrandsTrackingMap(displayedOffers),
        getIcoOfferTrackingMap(
          icoOffer,
          displayedOffers.some(({ brand }) => brand === BRANDS.ICO),
          kmxOffer?.valuation,
          vehicle
        )
      );

      return getMultiOfferResultsObject({
        offers: displayedOffers,
        vehicle,
        pageloadTrackingBySubactionName,
        offersArePending,
        createdAt,
        updatedAt,
        seller,
        source,
        partnersOffersFinalizedAt,
      });
    },
  },
  {
    path: offerDetailsPath.segment,
    async resolve(match, context) {
      const { modsId } = match;

      const record = await context.resolveValue(modsRecordPath.build(modsId), MultiOfferModel);

      if (isEmpty(record)) {
        return getOfferDetailsPageObject({ isError: true });
      }

      const partnerData = get(record, 'partners', {});

      // currently ODP page displays only CarMax offer.
      const partnerObject = partnerData[BRANDS.KMX];
      const offerData = get(partnerObject, ['offers', 0]);
      const offer = offerData && constructOfferObject(offerData, BRANDS.KMX, partnerObject);
      const icoOffer = getIcoOfferFromModsRecord(record);

      const vehicle = get(record, 'vehicle', {});
      const seller = get(record, 'seller', {});
      const offers = offer ? [offer] : [];

      const partnersToSolicit = get(record, 'partnersToSolicit');
      const isMultiOffer = hasMultiOfferPartners(partnersToSolicit);
      const solicitedPartners = getSolicitedPartners(partnersToSolicit);

      const isAccepted = offer && acceptedOfferFilter(offer);

      const pageloadTrackingBySubactionName = {
        ...getOfferCountTrackingMap(offers, record),
        ...(!isAccepted ? {} : getOfferDisplayCountTrackingMap(offers)),
        ...getOfferBrandsTrackingMap(offers),
        ...getSolicitedPartnersTrackingMap(solicitedPartners),
        ...getMultiOfferTrackingMap(isMultiOffer),
        ...getSellerAbTestTrackingMap(seller),
        ...getIcoOfferTrackingMap(icoOffer, false, offer?.valuation, vehicle),
        ...getModsSourceTrackingMap(record?.source),
      };

      return getOfferDetailsPageObject({
        offer,
        vehicle,
        pageloadTrackingBySubactionName,
        seller,
        isMultiOffer,
        partnersOffersFinalizedAt: record?.partnersOffersFinalizedAt,
        partnersToSolicit,
      });
    },
  },
  {
    path: searchByVinPath.segment,
    async resolve({ vin }, context) {
      try {
        const data = await withMetrics(EdmundsMultiOfferAPI, context).fetchJson(`/v1/offer-events/search-by-vin`, {
          headers: {
            ...VENOM_X_PRODUCT_ID_HEADER,
            'content-type': 'application/json',
          },
          method: 'POST',
          body: JSON.stringify({ vin: vin.toUpperCase() }),
        });
        const offerInfo = get(data, '0', {});
        return transformToModsData(offerInfo);
      } catch (e) {
        logger.warn(e, `Failed retrieving MODS record by VIN ${vin}`);
        return {};
      }
    },
  },
  {
    path: offerRenewalModsDataPath.segment,
    async resolve({ vin, modsId }, context) {
      if (modsId && modsId !== 'null') {
        const modsRecord = await context.resolveValue(modsRecordPath.build(modsId), MultiOfferModel);
        const kmxRefreshedOffers = modsRecord?.refreshedOffers?.kmx?.offers;
        if (!isEmpty(kmxRefreshedOffers)) {
          const modsData = transformToModsData(modsRecord);
          return {
            ...modsData,
            kmx: {
              ...modsData.kmx,
              // Take the latest refreshed offer.
              refreshedOffer: kmxRefreshedOffers[kmxRefreshedOffers.length - 1],
            },
          };
        }
      }
      return context.resolveValue(searchByVinPath.build(vin), MultiOfferModel);
    },
  },
  {
    path: prequalificationPath.segment,
    async resolve({ stateCode, dma, year, mileage, makeSlug }, context) {
      return makePrequalifyRequest(stateCode, dma, year, mileage, makeSlug, context);
    },
  },
  {
    /**
     * Used specifically to store the TMV parameters so it can be resolved in other paths
     * a resolver is not needed because we will be updating the value independently.
     *
     * See EMO-913 for more details.
     */
    path: tmvParamsPath.segment,
  },
  {
    path: tmvDataPath.segment,
    async resolve({ modsId }, context) {
      const dataFromTmvParams = await context.resolveValue(tmvParamsPath.build());
      let tmvSourceData = dataFromTmvParams;
      let vehicle = get(dataFromTmvParams, 'vehicle');
      let zipCode = get(dataFromTmvParams, 'seller.zipCode');

      if (isEmpty(dataFromTmvParams)) {
        // Get data directly from MODS if "tmvParams" is not available.
        const data = await context.resolveValue(modsRecordPath.build(modsId));
        tmvSourceData = data;
        vehicle = get(data, 'vehicle');
        zipCode = get(data, 'seller.zipCode');
      }

      if (!zipCode || isEmpty(vehicle)) return null;

      const tmvParams = {
        styleId: vehicle.edmundsStyleId,
        zipCode,
        mileage: vehicle.mileage,
        colorId: get(vehicle, 'colors.exterior.id'),
        optionIds: compact(get(vehicle, 'selectedOptions', []).map(({ id }) => id)).join(','),
      };

      let tmvData;
      try {
        tmvData = checkModsRecordForAbTestRecipe(tmvSourceData, 'EMO-4346', 'chal1')
          ? await getAllUsedTmvConditions(tmvParams, withMetrics(EdmundsGraphQLFederation, context))
          : await withMetrics(EdmundsAPI, context).fetchJson(buildAllTmvBandsUrl(tmvParams));
      } catch (e) {
        logger.warn(e, 'Failed TMV request');
        tmvData = { isError: true, message: e.message, status: getErrorStatusCode(e) };
      }

      return tmvData;
    },
  },
  {
    path: icoDealerPath.segment,
    async resolve({ zipCode }, context) {
      const icoDealersPath = buildClosestIcoDealersPath({
        zipCode,
        numIcoDealers: 1,
        voiType: 'newOptional,allOptional,usedOptional,noVehicle',
      });
      const icoDealers = await context.resolveValue(icoDealersPath, DealerIcoWidgetModel);
      const mainDealerId = icoDealers?.multiDealerIds?.[0];
      const dealerId = mainDealerId ?? icoDealers?.fallbackDealerIds?.[0];
      if (isNil(dealerId)) {
        return {};
      }
      const isFallbackDealer = isNil(mainDealerId);
      try {
        const queryParams = {
          fields:
            'location,address,franchises,name,showroomPage,productFeatures(types,productAttributes),make,phoneNumbers,partnerFeedMappings,logoUrl',
          zip: zipCode,
        };
        const queryString = Object.entries(queryParams)
          .map(([name, value]) => `${name}=${value}`)
          .join('&');
        const dealerInfo = await withMetrics(EdmundsAPI, context).fetchJson(
          `/dealer/v5/dealership/${dealerId}/franchises?${queryString}`
        );
        const { name, address, distance, phoneNumbers, franchises } = dealerInfo || {};
        return {
          id: dealerId,
          isFallbackDealer,
          name,
          address: pick(address, ['street', 'city', 'stateCode', 'zip']),
          distance: round(distance, 2),
          phoneNumbers,
          showroomPage: get(franchises, '[0].showroomPage'),
          invoicingAccount: findDealerInvoicingAccount(franchises),
          source: ICO_SOURCES.API,
        };
      } catch (e) {
        logger.warn(e, 'Failed dealership franchises request');
        return { id: dealerId, isFallbackDealer };
      }
    },
  },
  {
    path: icoOfferPath.segment,
    async resolve(match, context) {
      const { modsId } = match;
      const data = await context.resolveValue(modsRecordPath.build(modsId), MultiOfferModel);
      const icoOffer = getIcoOfferFromModsRecord(data);
      const rooftopId = icoOffer?.store?.rooftopId;
      const storeData = parseStoreRawData(icoOffer?.store?.rawData, false);

      if (!acceptedOfferFilter(icoOffer) || !rooftopId || isEmpty(storeData)) return null;

      const page = await context.resolveValue('page', PageModel);

      const isOdp = page?.name === PAGE_NAMES.OFFER_DETAILS_PAGE;
      const partnerData = get(data, 'partners', {});
      const partnerObject = partnerData[BRANDS.KMX];
      const offerData = get(partnerObject, ['offers', 0]);
      const isExpiredOdpPage = isOdp ? isExpiredOffer(offerData) : false;

      // We only need this data for non-expired ODP
      const dealerInfo =
        isOdp && !isExpiredOdpPage
          ? await withMetrics(EdmundsAPI, context).fetchJson(
              `/dealer/v5/dealership/${rooftopId}/franchises?fields=franchises,name,showroomPage`
            )
          : {};

      const { name, address, phoneNumber } = storeData;
      const { franchises } = dealerInfo;

      return {
        ...icoOffer,
        id: rooftopId,
        name,
        address,
        phoneNumbers: {
          basic: phoneNumber,
        },
        showroomPage: get(franchises, '[0].showroomPage'),
        isFallbackDealer: false, // MORW doesn't use fallback dealers
        source: ICO_SOURCES.MODS,
      };
    },
  },
  {
    path: inventoryModulePath.segment,
    async resolve({ make }, context) {
      const searchFilterData = await context.resolveValue('searchFilter', InventoryModel);

      try {
        const url = getInventoryModuleUrl({ ...searchFilterData, make });
        const data = await withMetrics(EdmundsAPI, context).fetchJson(url);

        return get(data, 'inventories', DEFAULT_INVENTORY);
      } catch {
        return DEFAULT_INVENTORY;
      }
    },
  },
  {
    path: partnersBrandAssetsPath.segment,
    async resolve(_, context) {
      try {
        return await withMetrics(EdmundsMultiOfferAPI, context).fetchJson('/v1/partners/brand-assets', {
          headers: {
            ...VENOM_X_PRODUCT_ID_HEADER,
          },
        });
      } catch (e) {
        logger.warn(e, 'Failed retrieving partners brand assets');
        return {};
      }
    },
  },
]);
